import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const entryOf = (location) => ({ key: location.key || 'initial', pathname: location.pathname });

// The history entries this tab has walked through inside the app, newest last
export const trackHistory = (stack, action, entry) => {
  if (action === 'PUSH') return [...stack, entry];
  if (action === 'REPLACE') return [...stack.slice(0, -1), entry];

  // POP is back or forward. Back lands on an entry already in the stack; anything else (forward,
  // a reload) leaves us not knowing what's behind, so start over from here.
  const at = stack.findIndex((e) => e.key === entry.key);

  return at === -1 ? [entry] : stack.slice(0, at + 1);
};

// How far to go back to leave the current page, or null when nothing in the app is behind it.
// Hash steps on the same page (a service moving between its songs) count as part of the page.
export const stepsBack = (stack) => {
  const here = stack.length - 1;
  const { pathname } = stack[here];

  for (let i = here - 1; i >= 0; i--) {
    if (stack[i].pathname !== pathname) return i - here;
  }

  return null;
};

/**
 * A back button's handler: back through history when the page was reached from inside the app,
 * so the browser's own back and swipe stay in step with it, or `fallback` when it was opened
 * from a link. Has to live somewhere mounted for the whole session to see every navigation.
 */
const useInAppBack = () => {
  const history = useHistory();
  const location = useLocation();
  const stack = useRef(null);

  if (stack.current === null) stack.current = [entryOf(location)];

  useEffect(() => {
    stack.current = trackHistory(stack.current, history.action, entryOf(location));
  }, [history, location]);

  return useCallback(
    (fallback) => {
      const steps = stepsBack(stack.current);

      if (steps === null) history.push(fallback);
      else history.go(steps);
    },
    [history],
  );
};

export default useInAppBack;
